// Kie.AI Video Generation Service
// Unified interface for all video generation models

const API_BASE_URL = `${process.env.REACT_APP_API_URL || 'https://api.daggpt.network'}/api/kei-ai-video`;

/**
 * Generate video using Veo 3.1 (Google)
 * @param {string} prompt - Text description for video
 * @param {Object} options - Generation options
 * @returns {Promise<Object>} Task response with taskId
 */
export async function generateVideoVeo31(prompt, options = {}) {
  const {
    imageInput = null,
    aspectRatio = '16:9',
    duration = 5,
  } = options;

  const response = await fetch(`${API_BASE_URL}/veo31/generate`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      prompt,
      image_input: imageInput,
      aspect_ratio: aspectRatio,
      duration,
    }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to generate video with Veo 3.1');
  }

  return response.json();
}

/**
 * Generate video using Runway Gen-3
 * @param {string} prompt - Text description for video
 * @param {Object} options - Generation options
 * @returns {Promise<Object>} Task response with taskId
 */
export async function generateVideoRunway(prompt, options = {}) {
  const {
    imageInput = null,
    aspectRatio = '16:9',
    duration = 5,
  } = options;

  const response = await fetch(`${API_BASE_URL}/runway/generate`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      prompt,
      image_input: imageInput,
      aspect_ratio: aspectRatio,
      duration,
    }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to generate video with Runway');
  }

  return response.json();
}

/**
 * Generate video using Sora 2 Pro
 * @param {string} prompt - Text description for video
 * @param {Object} options - Generation options
 * @returns {Promise<Object>} Task response with taskId
 */
export async function generateVideoSora2(prompt, options = {}) {
  const {
    imageInput = null,
    aspectRatio = '16:9',
    duration = 5,
  } = options;

  const response = await fetch(`${API_BASE_URL}/sora2/generate`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      prompt,
      image_input: imageInput,
      aspect_ratio: aspectRatio,
      duration,
    }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to generate video with Sora 2');
  }

  return response.json();
}

/**
 * Generate video using Kling 3.0
 * @param {string} prompt - Text description for video
 * @param {Object} options - Generation options
 * @returns {Promise<Object>} Task response with taskId
 */
export async function generateVideoKling(prompt, options = {}) {
  const {
    imageInput = null,
    aspectRatio = '16:9',
    duration = 5,
  } = options;

  const response = await fetch(`${API_BASE_URL}/kling/generate`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      prompt,
      image_input: imageInput,
      aspect_ratio: aspectRatio,
      duration,
    }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to generate video with Kling');
  }

  return response.json();
}

/**
 * Generate video using Grok Imagine Video
 * @param {string} prompt - Text description for video
 * @param {Object} options - Generation options
 * @returns {Promise<Object>} Task response with taskId
 */
export async function generateVideoGrokImagine(prompt, options = {}) {
  const {
    imageInput = null,
    aspectRatio = '16:9',
    duration = 3,
  } = options;

  const response = await fetch(`${API_BASE_URL}/grok-imagine/generate`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      prompt,
      image_input: imageInput,
      aspect_ratio: aspectRatio,
      duration,
    }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to generate video with Grok Imagine');
  }

  return response.json();
}

/**
 * Generate video using Wan 2.6
 * @param {string} prompt - Text description for video
 * @param {Object} options - Generation options
 * @returns {Promise<Object>} Task response with taskId
 */
export async function generateVideoWan(prompt, options = {}) {
  const {
    imageInput = null,
    videoInput = null,
    aspectRatio = '16:9',
    duration = 4,
  } = options;

  const response = await fetch(`${API_BASE_URL}/wan/generate`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      prompt,
      image_input: imageInput,
      video_input: videoInput,
      aspect_ratio: aspectRatio,
      duration,
    }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to generate video with Wan');
  }

  return response.json();
}

/**
 * Generate video using Bytedance Seedance
 * @param {string} prompt - Text description for video
 * @param {Object} options - Generation options
 * @returns {Promise<Object>} Task response with taskId
 */
export async function generateVideoSeedance(prompt, options = {}) {
  const {
    imageInput = null,
    aspectRatio = '16:9',
    duration = 5,
  } = options;

  const response = await fetch(`${API_BASE_URL}/seedance/generate`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      prompt,
      image_input: imageInput,
      aspect_ratio: aspectRatio,
      duration,
    }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to generate video with Seedance');
  }

  return response.json();
}

/**
 * Poll video generation task status
 * @param {string} taskId - Task ID from generation request
 * @param {number} maxAttempts - Maximum polling attempts (default: 60)
 * @param {number} intervalMs - Polling interval in milliseconds (default: 10000)
 * @returns {Promise<Object>} Final task status with video URL
 */
export async function pollVideoTaskStatus(taskId, maxAttempts = 60, intervalMs = 10000) {
  let attempts = 0;

  const poll = async () => {
    attempts++;
    console.log(`Polling video task status (attempt ${attempts}/${maxAttempts})...`);

    const response = await fetch(`${API_BASE_URL}/task-status/${taskId}`);
    
    if (!response.ok) {
      throw new Error('Failed to check video task status');
    }

    const statusData = await response.json();
    console.log('Video task status:', statusData);

    // Check if task is completed
    if (statusData.status === 'completed' && statusData.videoUrl) {
      return statusData;
    }

    // Check if task failed
    if (statusData.status === 'failed') {
      throw new Error(statusData.error || 'Video generation failed');
    }

    // Continue polling if not completed and haven't exceeded max attempts
    if (attempts < maxAttempts) {
      await new Promise(resolve => setTimeout(resolve, intervalMs));
      return poll();
    } else {
      throw new Error('Video generation timeout - exceeded maximum polling attempts');
    }
  };

  return poll();
}

/**
 * Upload reference media (image or video) to R2 for video generation
 * @param {File} file - Image or video file
 * @returns {Promise<string>} Public URL of uploaded file
 */
export async function uploadReferenceMedia(file) {
  const formData = new FormData();
  formData.append('file', file);

  const response = await fetch(`${API_BASE_URL}/upload-reference`, {
    method: 'POST',
    body: formData,
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to upload reference media');
  }

  const data = await response.json();
  return data.url;
}

/**
 * Unified video generation function that routes to the appropriate model
 * @param {string} modelId - Model identifier
 * @param {string} prompt - Text description
 * @param {Object} options - Generation options
 * @returns {Promise<Object>} Generated video data
 */
export async function generateVideo(modelId, prompt, options = {}) {
  console.log(`Generating video with ${modelId}...`);

  let taskResponse;

  // Route to appropriate model
  switch (modelId) {
    case 'veo-3.1':
      taskResponse = await generateVideoVeo31(prompt, options);
      break;
    case 'runway-gen3':
      taskResponse = await generateVideoRunway(prompt, options);
      break;
    case 'sora-2-pro':
      taskResponse = await generateVideoSora2(prompt, options);
      break;
    case 'kling-3.0':
      taskResponse = await generateVideoKling(prompt, options);
      break;
    case 'grok-imagine-video':
      taskResponse = await generateVideoGrokImagine(prompt, options);
      break;
    case 'wan-2.6':
      taskResponse = await generateVideoWan(prompt, options);
      break;
    case 'seedance-1.5':
      taskResponse = await generateVideoSeedance(prompt, options);
      break;
    default:
      throw new Error(`Unknown video model: ${modelId}`);
  }

  // Poll for completion
  const result = await pollVideoTaskStatus(taskResponse.taskId);

  return {
    videoUrl: result.videoUrl,
    thumbnailUrl: result.thumbnailUrl,
    taskId: taskResponse.taskId,
    model: modelId,
  };
}

/**
 * Submit video generation job WITHOUT polling - returns taskId immediately.
 * The Worker webhook will handle completion and R2 upload asynchronously.
 * Frontend should poll GET /api/jobs/:taskId via contentService.pollJob().
 * @param {string} modelId  - Model identifier
 * @param {string} prompt   - Text prompt
 * @param {Object} options  - { imageInput, videoInput, aspectRatio, duration }
 * @param {string} userId   - User wallet address (for job registration)
 * @returns {Promise<{taskId: string}>}
 */
export async function generateVideoSubmit(modelId, prompt, options = {}, userId = '') {
  const { imageInput, videoInput, aspectRatio, duration } = options;

  // Map model id to express route
  const routeMap = {
    'veo-3.1':            `${API_BASE_URL}/veo31/generate`,
    'runway-gen3':        `${API_BASE_URL}/runway/generate`,
    'sora-2-pro':         `${API_BASE_URL}/sora2/generate`,
    'kling-3.0':          `${API_BASE_URL}/kling/generate`,
    'grok-imagine':       `${API_BASE_URL}/grok-imagine/generate`,
    'grok-imagine-video': `${API_BASE_URL}/grok-imagine/generate`,
    'wan-2.6':            `${API_BASE_URL}/wan/generate`,
    'seedance-1.5':       `${API_BASE_URL}/seedance/generate`,
    'seedance-2.0':       `${API_BASE_URL}/seedance-2/generate`,
  };

  const isGeneric = !routeMap[modelId];
  const url = isGeneric ? `${API_BASE_URL}/generic/generate` : routeMap[modelId];

  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(userId ? { 'x-user-id': userId } : {}),
    },
    body: JSON.stringify({
      model_id: modelId,
      prompt,
      image_urls: imageInput ? [imageInput] : null,
      first_frame_url: imageInput || null,
      image_input: imageInput || null, // compatibility for older endpoints
      video_input: videoInput || null,
      aspect_ratio: aspectRatio || '16:9',
      duration: duration || 5,
    }),
  });

  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.error || `Failed to submit ${modelId} video job`);
  }

  return response.json(); // { success, taskId }
}

export default {
  generateVideo,
  generateVideoSubmit,
  generateVideoVeo31,
  generateVideoRunway,
  generateVideoSora2,
  generateVideoKling,
  generateVideoGrokImagine,
  generateVideoWan,
  generateVideoSeedance,
  pollVideoTaskStatus,
  uploadReferenceMedia,
};
